import { QueryClient } from '@tanstack/react-query';
import { API_ERROR_TYPES, isApiError, type ApiError } from '../api/fetch/apiClient';
import { isShopNotOpenError } from './shopNotOpen';

type GlobalErrorHandler = (error: ApiError) => void;

let globalErrorHandler: GlobalErrorHandler | null = null;

export const setGlobalErrorHandler = (handler: GlobalErrorHandler): void => {
  globalErrorHandler = handler;
};

export const clearGlobalErrorHandler = (): void => {
  globalErrorHandler = null;
};

const isGlobalError = (error: unknown): error is ApiError =>
  isApiError(error) && error.type === API_ERROR_TYPES.NETWORK;

// Login and guest creation refuse with a 403 + notify.auth.shop_not_open
// (isShopNotOpenError) when the shop is not open to the requester
// (docs/superpowers/specs/2026-10-22-remove-sandbox-mode-design.md §6.3).
// Refetching the account info brings in the shop state, so MainLayout shows
// ShopClosedPage or the paused notice. accounts/info itself answers every
// visitor and never 403s with this error, so invalidating it cannot recurse.

// A 401 means the session was rejected — e.g. the shop just closed, which
// rejects every customer session (§6.2). Re-check the session: /users/me's
// own 401 handling (useUserData) then drops the token and, for a closed
// shop, lands on ShopClosedPage, whichever request hit the rejection first.
// /users/me resolves its 401 to "logged out" rather than an error, so this
// cannot recurse.
const isUnauthorizedError = (error: unknown): boolean => isApiError(error) && error.status === 401;

// The account info (and with it the shop state) is cached for minutes, so a
// customer browsing when the shop stops taking orders first hears of it as a
// plain 403 (an album page, Pay now). Refetch it then, so the paused notice
// takes over. Only for a logged-in caller — an anonymous 403 (a logged-in-only
// page) says nothing about the shop. 'token' is TOKEN_KEY in
// api/queryHooks/user.ts, which imports this module.
const isAuthenticatedForbiddenError = (error: unknown): boolean =>
  isApiError(error) && error.status === 403 && localStorage.getItem('token') !== null;

// Every backstop joins a refetch already in flight (cancelRefetch: false)
// instead of restarting it. Several requests fail together when a session is
// rejected; a restart would leave the cancelled /users/me run going (its
// queryFn takes no abort signal) and each run would drop the token and toast
// on its own.
const handleQueryError = (error: unknown): void => {
  if (isGlobalError(error) && globalErrorHandler) {
    globalErrorHandler(error);
  }
  if (isShopNotOpenError(error) || isAuthenticatedForbiddenError(error)) {
    queryClient.invalidateQueries({ queryKey: ['accountInfo'] }, { cancelRefetch: false });
  }
  if (isUnauthorizedError(error)) {
    queryClient.invalidateQueries({ queryKey: ['login'] }, { cancelRefetch: false });
  }
};

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, error) => {
        if (isApiError(error) && error.type === API_ERROR_TYPES.CLIENT) return false;
        return failureCount < 2;
      },
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 10000),
      staleTime: 30 * 1000,
      throwOnError: false,
    },
    mutations: {
      retry: false,
    },
  },
});

queryClient.getQueryCache().subscribe((event) => {
  if (event.type === 'updated' && event.query.state.status === 'error') {
    handleQueryError(event.query.state.error);
  }
});

queryClient.getMutationCache().subscribe((event) => {
  if (event.type === 'updated' && event.mutation?.state.status === 'error') {
    handleQueryError(event.mutation.state.error);
  }
});

export default queryClient;
